import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import * as authApi from "../api/auth";
import { api } from "../api/client";
import type { Entitlements, User } from "../api/types";
import { canEdit, isOrgAdmin } from "../lib/dashboard";
import { allows } from "../lib/edition";

type Status = "loading" | "anonymous" | "authenticated";

interface Session {
  status: Status;
  user: User | null;
  login(username: string, password: string): Promise<void>;
  logout(): void;
  /** Agree to the current terms; the session's user then carries the new agreement. */
  acceptTerms(version: string): Promise<void>;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  /** False for a viewer: the server refuses every change they could attempt. */
  canEdit: boolean;
  /** Edition and open features (null until loaded); `has` reads it, counting unknown as open. */
  entitlements: Entitlements | null;
  has(feature: string): boolean;
}

/** The caller's features, or null if the server cannot say (the server enforces either way). */
const loadEntitlements = () => authApi.features().catch(() => null);

const Ctx = createContext<Session | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>("loading");
  const [user, setUser] = useState<User | null>(null);
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);

  const logout = useCallback(() => {
    authApi.logout();
    setUser(null);
    setEntitlements(null);
    setStatus("anonymous");
    window.location.hash = "#/login";
  }, []);

  // Restore a previous session from the stored refresh token, if there is one.
  useEffect(() => {
    api.onSessionExpired = () => { setUser(null); setStatus("anonymous"); window.location.hash = "#/login"; };
    let alive = true;
    (async () => {
      if (api.hasRefreshToken && (await api.refresh())) {
        try {
          const [u, e] = await Promise.all([authApi.me(), loadEntitlements()]);
          if (alive) { setUser(u); setEntitlements(e); setStatus("authenticated"); return; }
        } catch { /* fall through to anonymous */ }
      }
      if (alive) setStatus("anonymous");
    })();
    return () => { alive = false; };
  }, []);

  const login = useCallback(async (username: string, password: string) => {
    await authApi.login(username, password);
    const [u, e] = await Promise.all([authApi.me(), loadEntitlements()]);
    setUser(u);
    setEntitlements(e);
    setStatus("authenticated");
    window.location.hash = "#/";
  }, []);

  const acceptTerms = useCallback(async (version: string) => {
    setUser(await authApi.acceptTerms(version));
  }, []);

  const value = useMemo<Session>(
    () => ({
      status, user, login, logout, acceptTerms, isAdmin: isOrgAdmin(user?.role), isSuperAdmin: user?.role === "super_admin", canEdit: canEdit(user?.role),
      entitlements, has: (feature: string) => allows(entitlements, feature),
    }),
    [status, user, login, logout, acceptTerms, entitlements],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession outside SessionProvider");
  return s;
}
